import { isFrozenType, isPrimitiveType, isIdentifierType, isReferenceType, getRoot } from '@invisr/mobx-state-tree'
import _set from 'lodash/set'
import get from 'lodash/get'
import toArray from 'lodash/toArray'
import Mapper from '../store/models/props-mapper'

function getPath (path, node) {
  return [...path, Array.isArray(node._parent.storedValue) ? parseInt(node._subpath) : node._subpath]
}

function getChildrenNodes (node) {
  const ch = node.getChildren()
  if (ch.length) return ch
  return toArray(node._childNodes)
}

function * walk (target, path = [], visited = new WeakSet()) {
  const node = target.$treenode
  if (visited.has(target)) {
    yield { target, path, node, isParent: true }
    return
  }
  visited.add(target)
  const done = yield { target, path, node, isParent: true }

  if (done === true) return
  const parents = []
  const children = []
  const references = []

  for (const child of getChildrenNodes(node)) {
    if (child.value != null || child.storedValue != null) {
      if (isPrimitiveType(child.type) || isIdentifierType(child.type) || isFrozenType(child.type)) {
        children.push([child.value, child])
      } else {
        if (isReferenceType(child.type)) {
          if (child.value) references.push([child.value, child, getPath(path, child)])
        } else {
          parents.push([child.value || child.value, child])
        }
      }
    }
  }
  for (const [target, node] of parents) {
    yield * walk(target, getPath(path, node), visited)
  }
  for (const [target, , path] of references) {
    yield * walk(target, path, visited)
  }
  for (const [target, node] of children) {
    yield { target, path: [...path, node._subpath], node }
  }
}

function set (obj, path, value) {
  if (!path || path.length === 0) Object.assign(obj, value)
  else _set(obj, path, value)
}

export function cloneJS (mstObj, shouldCloneFn = () => true) {
  let clone = {}
  const cloneMap = new WeakMap()
  const walker = walk(mstObj)
  let o, exit
  while (!(o = walker.next(exit)).done) {
    exit = false
    const { target, node, path, isParent } = o.value
    if (target == null) continue
    if (isParent) {
      if (cloneMap.has(target)) {
        set(clone, path, cloneMap.get(target))
        continue
      }
      if (Array.isArray(target)) {
        set(clone, path, [])
      } else {
        let obj = shouldCloneFn(o.value)
        if (!obj) {
          if (path.length) set(clone, path, target)
          else clone = target
          exit = true
          continue
        } else {
          if (obj === true) obj = {}
          if (!path.length) clone = obj
          else set(clone, path, obj)
          const initializers = node.type.initializers
          initializers.forEach(init => init(obj, true))
        }
      }
      const newObj = path.length ? get(clone, path) : clone
      const lastPath = path.slice(0, -1)
      const lastParent = path.length === 0 ? mstObj?.$treenode?.parent?.storedValue : lastPath.length ? get(clone, lastPath) : clone
      newObj.$treenode = {
        $mstFake: true,
        parent: lastParent?.$treenode,
        storedValue: newObj,
        value: newObj,
        snapshot: newObj,
        type: node.type,
        root: getRoot(mstObj)?.$treenode,
        assertAlive: function () {}
      }
      cloneMap.set(target, newObj)
    } else {
      if (get(clone, path) === undefined) set(clone, path, target)
    }
  }
  return clone
}

export function cloneComponent (mstObj, props) {
  if(mstObj.$treenode?.$mstFake) return mstObj
  const index = props.index ?? 0
  const renderProps = {
    [mstObj.id + '_' + index]: props
  }
  return cloneJS(mstObj, ({ path, target }) => {
    const key = path[path.length - 1]
    if (key === 'dataSource') return false
    if (target.$treenode.type.name.includes('Mapping')) {
      const mapping = {}
      mapping._mapper = new Mapper(mapping, renderProps, mstObj.id + '_' + index + '.' + path.join('.'))
      return mapping
    }
    if (target.id) return { id: target.id + '_' + index }
    return {}
  })
}
